/**
 * @author NikolaV
 */
import { latLonToEN, mapRotationDegrees } from '@openlayers/ol-utils';
import { ImageViewerWithNavigation } from '@pages/map/actions/ImageViewerWithNavigation';
import { Events, EventsGlobal } from '@shared/Events';
import { ImagesStore } from '@shared/images/ImagesStore';
import { TreeNode } from '@shared/ui-native/Tree';
import { reaction } from 'mobx';
import { easeOut } from 'ol/easing';
import { Point as OlPoint } from 'ol/geom';
import OlCluster from 'ol/source/Cluster';
import * as React from 'react';
import { ILayerWrapperOptions, LayerWrapper } from '../LayerWrapper';
import { ImageFeatureFactory } from './ImageFeatureFactory';
import { WindowManager } from '@shared/ui-react/FloatingWindow';


export class ImagesLayer extends LayerWrapper<IImage> {
    featureType: FeatureType = 'image';
    clusterSource: OlCluster = null;

    constructor(opts: ILayerWrapperOptions<IImage>,
        private imagesStore: ImagesStore,
        private getTreeNodeById: (id: number) => TreeNode) {
        super(opts);

        this.clusterSource = new OlCluster({
            distance: 20,
            source: this.layerSource,
        });

        this.layer.setSource(this.clusterSource);
        this.layer.set('id', this.featureType);

        // icon scale
        this.setStyleFunction(feature => {
            return ImageFeatureFactory.styleFunction(feature, this.imagesStore.iconScale, mapRotationDegrees(this.map));
        });
        reaction(() => this.imagesStore.iconScale, () => this.layerSource.dispatchEvent('change'));

        // when items or filter change
        reaction(() => this.imagesStore.itemsFiltered, this.redrawDebounced);
        reaction(() => this.imagesStore.items, this.redrawDebounced);
        this.redraw();
    }

    // make fresh features
    redraw = () => {
        this.allFeatures = [];
        this.layerSource.clear(true);
        this.drawAll(this.imagesStore.itemsFiltered);
        this.layerSource.dispatchEvent('change');
    }

    redrawDebounced = _.debounce(this.redraw, 100);

    onClose(): void {
        Events.trigger('map-hide-overlays');
    }

    async openImageByUuid(uuid: string, imagesGroupIds: number[] = null, centerOn = true) {
        let image: any = this.imagesStore.getByUuid(uuid);
        if (image) {
            this.openImageById(image.id, imagesGroupIds, centerOn);
        }
    }

    async openImageById(id: number, imagesGroupIds: number[] = null, centerOn = true, onClose?: () => void) {

        if (!id) return;
        let image = this.imagesStore.getById(id);

        if (!image) {
            await this.imagesStore.getImageById(id).done(res => {
                image = res.ajaxImage;
            });
            if (!image) return;
        }

        let allVisibleItems = this.imagesStore.itemsFiltered;
        if (Array.isArray(imagesGroupIds)) {
            allVisibleItems = imagesGroupIds.map(id => this.imagesStore.items.find(i => i.id == id));
        }  else if (!allVisibleItems.find(a => a.id === id)) {
            allVisibleItems = [image]
        }

        let instance: ImageViewerWithNavigation = null;
        WindowManager.open({
            group: 'image-viewer',
            id: String(id),
            showCloseButton: true,
            allowMultiple: true,
            resize: 'both',
            collapsedTitle: image.name,
            onClose,
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'ArrowLeft') instance.goPrev();
                else if (e.key === 'ArrowRight') instance.goNext();
                else if (e.key === 'Home') instance.goFirst();
                else if (e.key === 'End') instance.goLast();
            },
            body: <ImageViewerWithNavigation
                ref={el => instance = el}
                initialId={image.id}
                queue={allVisibleItems}
                onEdit={item => Events.trigger('map-edit-image', item)}
                onOpenFolderId={(folderId, imageId) => Events.trigger(EventsGlobal.MAP_OPEN_IMAGE_FOLDER_WITH_THUMBNAILS, { folderId, imageId })}
                folderTreeNode={this.getTreeNodeById(image.folderId)}
                isImageCluster={Array.isArray(imagesGroupIds)}
                isMobile={window.location.href.includes('mobile2.htm')}
                onCenter={image => this.centerAndHighlightImage(image)}
                getAllPanoramas={() => this.imagesStore.itemsFiltered.filter(i => i.panorama)}
            />
        })

        if (!image.geographicPoint || (0 == image.geographicPoint.lat && 0 == image.geographicPoint.lon)) {
            // do not pan to zero latlon
            return;
        }

        if (centerOn) {
            this.centerAndHighlightImage(image);
        } else {
            const feature = this.getFeatureById(image.id);
            Events.trigger('map-highlight-feature', feature)
        }
    }

    fetchAndDrawFolder(): JQueryPromise<any> {
        return $.Deferred().resolve();
    }

    centerAndHighlightImage = (image: IImage) => {
        let padding = [1, 1, 1, 1];
        let availWidth = window.innerWidth;
        if (document.querySelector('#map-sidebar-holder')) { // desktop has sidebar
            padding = [window.innerHeight / 2, 1, 1, 1];
        } else { // mobile
            padding = [1, 1, 40, 1];
            WindowManager.toggleMinimizeTopmost();
        }
        const coord = latLonToEN(image.geographicPoint.lat, image.geographicPoint.lon);
        if (coord) {
            this.map.getView().fit(
                new OlPoint(latLonToEN(image.geographicPoint.lat, image.geographicPoint.lon)),
                {
                    padding,
                    duration: 250,
                    easing: easeOut,
                    maxZoom: this.map.getView().getZoom(),
                });
        }

        const feature = this.getFeatureById(image.id);
        Events.trigger('map-highlight-feature', feature)
    }
}
